import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export function LoadingState(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div role="status" aria-live="polite" className="grid place-items-center py-16 text-neutral-500">
      <span className="animate-pulse">{t('common.loading')}</span>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title?: string;
  description?: string;
  action?: ReactNode;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="grid place-items-center py-16 text-center">
      <div>
        <p className="text-base font-semibold">{title ?? t('common.empty')}</p>
        {description ? <p className="mt-1 text-sm text-neutral-500">{description}</p> : null}
      </div>
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function ErrorState({
  title,
  description,
  onRetry,
}: {
  title?: string;
  description?: string;
  onRetry?: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div role="alert" className="grid place-items-center py-16 text-center">
      <div>
        <p className="text-base font-semibold text-red-700">
          {title ?? t('common.error_generic')}
        </p>
        {description ? <p className="mt-1 text-sm text-neutral-500">{description}</p> : null}
      </div>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 rounded-xl bg-[#9C7848] px-5 py-2 text-sm font-semibold text-white"
        >
          {t('common.retry')}
        </button>
      ) : null}
    </div>
  );
}

export function OfflineState({ description }: { description?: string }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div role="status" className="grid place-items-center py-16 text-center text-neutral-500">
      <div>
        <p className="text-base font-semibold">{t('common.offline')}</p>
        {description ? <p className="mt-1 text-sm">{description}</p> : null}
      </div>
    </div>
  );
}
